// Constants
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { KeyCode } from "@ms/uno-constants/lib/local/KeyCode";
// Controls
import { DirectionalHint, Icon } from "@fluentui/react";
import { ContextualMenu } from "../../../contextualMenu/ContextualMenu";
import { LabelTag } from "../../labelTag/LabelTag";
import { ProgressState } from "@ms/uno-models/lib/local/client/legacyPlanner/progressState/ProgressState";
import { TaskKind } from "@ms/uno-models/lib/local/client/legacyPlanner/task/ITask";
// Strings
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
// Styles
import { classNamesFunction, styled, useTheme } from "@fluentui/react";
import { assignIconProps, checkMarkIconProps, chevronRightIconProps, copyIconProps, deleteIconProps, linkIconProps, moreIconProps, moveIconProps, tagIconProps } from "../../../../icons/icons.styles";
import { getStyles } from "./TaskContextMenu.styles";
// Utilities
import { ConvertPercentCompleteToProgressState } from "@ms/uno-models/lib/local/client/legacyPlanner/utilities/ConversionUtilities";
import { getLabelPlaceholderByIndex } from "@ms/uno-models/lib/local/utilities/LabelUtilities";
import { KeyboardUtilities } from "@ms/uno-utilities/lib/local/KeyboardUtilities";
import isArray from "lodash/isArray";
import React, { useCallback, useRef } from "react";
import { useLoggers } from "../../../../hooks/useLoggers";
import { useVisibleState } from "../../../../hooks/useVisibleState";
import { getMyDayContextMenuItem } from "../../../../utilities/ContextualMenuUtilities";
import { sortLabelsByUsageAndIndex } from "../../../../utilities/LabelUtilities";
import { getContextualMenuStyles } from "../../../contextualMenu/ContextualMenu.styles";
/** Callback to generate classnames for the component styles */ const getClassNames = classNamesFunction();
/** Base component to be wrapped with `styled` */ function TaskContextMenuBase({ currentUserId, config, task, labels, showLabel, showAssign, showCopyTask, showCopyLinkToTask, showTaskDetails, showDelete, isFocusZoneEnabled, otherCommandsMenuCommandButton, styles, disableFocusZone, fetchTaskContainerPermissions, onFocusContainer, onUpdateTask, onClickAssign, onOtherCommandsMenuOpened, deleteTask, openCopyTaskDialog, openMoveTaskDialog, openDeleteRecurringTaskDialog, onCopyLinkToTask, openTaskDetails, toggleMyDayForTask }) {
    const { engagementLogger } = useLoggers();
    const theme = useTheme();
    //#region State
    /** Tracks last key down */ const lastKeyDown = useRef(null);
    /** Ref to the context menu button */ const taskContextMenuButtonRef = useRef(null);
    /** People picker visible state and callbacks */ const { isItemVisible: isTaskContextMenuVisible, toggleOnEvent: toggleTaskContextMenu, hideItem: hideTaskContextMenu } = useVisibleState();
    //#endregion
    //#region Utilities
    const getLabelMenuItem = ()=>{
        if (!showLabel || labels == null || labels.length === 0) {
            return null;
        }
        const sortedLabels = sortLabelsByUsageAndIndex(labels);
        const labelItems = sortedLabels.map((label)=>({
                key: label.index.toString(),
                data: label,
                onRender: onRenderLabelMenuItem
            }));
        return {
            key: "Labels",
            name: Strings.TaskContextMenu_LabelOption,
            iconProps: tagIconProps,
            subMenuProps: {
                items: labelItems
            },
            submenuIconProps: chevronRightIconProps
        };
    };
    /** Log engagement with component name */ const logEngagement = useCallback((action, extraData, exportName)=>{
        engagementLogger.logEngagement(EngagementTelemetryConstants.TaskCardContextMenu, action, extraData, exportName);
    }, [
        engagementLogger
    ]);
    //#endregion
    //#region Callbacks
    /**
     * Toggle the other commands menu open/closed
     * @param event Simple user interface events
     */ const toggleOtherCommandsMenu = (event)=>{
        event.stopPropagation();
        if (task.creationSource?.publication != null) {
            fetchTaskContainerPermissions(task);
        }
        if (!isTaskContextMenuVisible) {
            logEngagement(EngagementTelemetryConstants.TaskCardContextMenuClicked, {
                taskCreationSourceType: task.taskCreationSourceType ?? ""
            });
        }
        toggleTaskContextMenu(event);
        onOtherCommandsMenuOpened?.();
    };
    /**
     * Toggle the other commands menu open/closed
     * @param event Simple user interface events
     */ const handleOtherCommandsMenu = (event)=>{
        const key = KeyboardUtilities.getKey(event);
        // Make sure the key press began on this element
        if (key === lastKeyDown.current && key === KeyCode.ENTER) {
            event.stopPropagation();
            toggleOtherCommandsMenu(event);
        }
        lastKeyDown.current = null;
    };
    /**
     * Called on key down on the task context menu
     * @param event Simple user interface events
     */ const handleOnKeyDown = (event)=>{
        const key = KeyboardUtilities.getKey(event);
        if (key === KeyCode.ENTER) {
            event.stopPropagation();
        }
        lastKeyDown.current = key;
        disableFocusZone(event);
    };
    /** Callback for opening copy task dialog */ const onClickCopyTask = useCallback(()=>{
        logEngagement(EngagementTelemetryConstants.CopyTaskDialogOpened);
        openCopyTaskDialog?.(task.id);
    }, [
        logEngagement,
        task.id,
        openCopyTaskDialog
    ]);
    /** Copy link to task */ const onClickCopyLinkToTask = useCallback(()=>{
        logEngagement(EngagementTelemetryConstants.TaskLinkCopied, {
            taskId: task.id
        });
        onCopyLinkToTask?.(task.id);
        onFocusContainer?.();
    }, [
        logEngagement,
        task.id,
        onCopyLinkToTask,
        onFocusContainer
    ]);
    /** Callback for opening move task dialog */ const onClickMoveTask = useCallback(()=>{
        logEngagement(EngagementTelemetryConstants.MoveTaskDialogOpened);
        openMoveTaskDialog?.(task.id);
    }, [
        logEngagement,
        task.id,
        openMoveTaskDialog
    ]);
    /** Handle delete task */ const handleDelete = useCallback(()=>{
        if (task.recurrence?.isActive()) {
            logEngagement(EngagementTelemetryConstants.DeleteRecurringTaskDialogOpened);
            openDeleteRecurringTaskDialog?.(task.id);
        } else {
            deleteTask?.(task.id);
        }
    }, [
        logEngagement,
        task.id,
        task.recurrence,
        openDeleteRecurringTaskDialog,
        deleteTask
    ]);
    /**
     * Apply or remove a category from the task
     * @param categoryIndex Index of the category being toggled
     */ const toggleCategory = (categoryIndex)=>{
        const updatedTask = task.getClone();
        const appliedCategoryIndex = updatedTask.appliedCategories.indexOf(categoryIndex);
        if (appliedCategoryIndex > -1) {
            // Remove it
            updatedTask.appliedCategories.splice(appliedCategoryIndex, 1);
            logEngagement(EngagementTelemetryConstants.RemoveLabelFromTask);
        } else {
            // Add it
            updatedTask.appliedCategories.push(categoryIndex);
            logEngagement(EngagementTelemetryConstants.AddLabelToTask);
        }
        onFocusContainer?.();
        onUpdateTask?.(task, updatedTask);
    };
    //#endregion
    //#region Render
    const classNames = getClassNames(styles, {
        theme,
        isMenuOpen: isTaskContextMenuVisible
    });
    /**
     * Render a menu item for the given item data
     * @param item IContextualMenuItem containing an ILabel
     */ const onRenderLabelMenuItem = (item)=>{
        const label = item.data;
        const { index } = label;
        const taskAppliedCats = isArray(task.appliedCategories) ? task.appliedCategories : [];
        const isChecked = taskAppliedCats.indexOf(index) > -1;
        const labelName = label.name || getLabelPlaceholderByIndex(index);
        const itemButtonProperties = {
            className: classNames.labelMenuItem,
            name: labelName,
            "aria-posinset": item["aria-posinset"],
            "aria-setsize": item["aria-setsize"],
            "aria-checked": isChecked
        };
        return /*#__PURE__*/ React.createElement("button", {
            ...itemButtonProperties,
            onClick: ()=>toggleCategory(index),
            role: "menuitemcheckbox"
        }, isChecked ? /*#__PURE__*/ React.createElement(Icon, checkMarkIconProps) : /*#__PURE__*/ React.createElement(Icon, {
            iconName: ""
        }), /*#__PURE__*/ React.createElement(LabelTag, {
            key: index,
            index: index,
            title: labelName,
            isCollapsed: false
        }));
    };
    /** Render the task context menu */ const renderContextMenu = ()=>{
        const menuItems = [];
        if (toggleMyDayForTask) {
            const onMyDayMenuItemClick = ()=>{
                toggleMyDayForTask(task.id);
            };
            const myDayContextMenuItem = getMyDayContextMenuItem(task.isOnMyDay, onMyDayMenuItemClick);
            if (!task.isEligibleForMyDay(currentUserId)) {
                myDayContextMenuItem.disabled = true;
                myDayContextMenuItem.title = Strings.TaskContextMenu_AddToMyDayDisabledTooltip;
            }
            menuItems.push(myDayContextMenuItem);
        }
        const labelMenuItem = getLabelMenuItem();
        if (labelMenuItem) {
            menuItems.push(labelMenuItem);
        }
        if (showAssign && onClickAssign && ConvertPercentCompleteToProgressState(task.percentComplete) !== ProgressState.Complete) {
            menuItems.push({
                key: "Assign",
                name: Strings.TaskContextMenu_AssignOption,
                iconProps: assignIconProps,
                onClick: onClickAssign
            });
        }
        menuItems.push({
            key: "CopyTask",
            name: Strings.TaskContextMenu_CopyTaskOption,
            iconProps: copyIconProps,
            onClick: onClickCopyTask,
            disabled: !showCopyTask
        });
        if (showCopyLinkToTask) {
            menuItems.push({
                key: "CopyLinkToTask",
                name: Strings.TaskContextMenu_CopyLinkToTaskOption,
                iconProps: linkIconProps,
                onClick: onClickCopyLinkToTask,
                disabled: task.kind === TaskKind.Todo
            });
        }
        menuItems.push({
            key: "MoveTask",
            name: Strings.TaskContextMenu_MoveTaskOption,
            iconProps: moveIconProps,
            onClick: onClickMoveTask,
            disabled: !config.enableMoveTaskOperation
        });
        if (showTaskDetails && openTaskDetails) {
            menuItems.push({
                key: "TaskDetails",
                name: Strings.TaskContextMenu_TaskDetailsOption,
                onClick: ()=>{
                    openTaskDetails(task.id, ()=>{});
                }
            });
        }
        if (showDelete) {
            menuItems.push({
                key: "Delete",
                name: Strings.TaskContextMenu_DeleteOption,
                iconProps: deleteIconProps,
                onClick: handleDelete
            });
        }
        return /*#__PURE__*/ React.createElement(ContextualMenu, {
            shouldFocusOnMount: true,
            items: menuItems,
            directionalHint: DirectionalHint.bottomAutoEdge,
            target: otherCommandsMenuCommandButton ?? taskContextMenuButtonRef,
            onDismiss: hideTaskContextMenu,
            styles: getContextualMenuStyles(theme, classNames.subComponentStyles.contextualMenu)
        });
    };
    return /*#__PURE__*/ React.createElement("div", {
        className: classNames.root,
        onClick: toggleOtherCommandsMenu,
        title: Strings.TaskContextMenu_MoreButtonTooltipText,
        "aria-label": Strings.TaskContextMenu_MoreButtonTooltipText,
        "aria-expanded": isTaskContextMenuVisible,
        "aria-haspopup": true,
        onKeyUp: handleOtherCommandsMenu,
        onKeyDown: handleOnKeyDown,
        "data-is-focusable": isFocusZoneEnabled,
        ref: otherCommandsMenuCommandButton ?? taskContextMenuButtonRef,
        role: "button"
    }, /*#__PURE__*/ React.createElement(Icon, moreIconProps), isTaskContextMenuVisible ? renderContextMenu() : null);
//#endregion
}
/**
 * Task context menu component
 */ export const TaskContextMenu = styled(TaskContextMenuBase, getStyles);
